import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { createDriver, updateDriver } from '@/api/users/drivers'
import formatter from '@/services/formatter'
import { getDriversRequest } from '@/store/modules/users/drivers/reducer'
import useListState from '@/hooks/useListState'

import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@material-ui/core'
import { PencilSimple, UserPlus } from 'phosphor-react'

import { Status } from '@/components/Status'
import { StyledTableContainer, StyledTableCell as TableCell } from '../styles'

import { DriverFormEdit } from './FormEdit'

// -------------------------------------------------

export function DriversList() {
  const { drivers, loading: driversLoading } = useSelector(
    (state) => state.users.drivers
  )

  const dispatch = useDispatch()

  const driversList = useListState(drivers)

  const [searchTerm, setSearchTerm] = useState('')
  const [currentDriver, setCurrentDriver] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const filteredDrivers = useMemo(() => {
    if (searchTerm.trim().length === 0) return driversList.list

    return driversList.list.filter((driver) =>
      [driver.wem_nome, driver.wem_cpf, driver.wem_rg]
        .join('')
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
  }, [driversList.list, searchTerm])

  function handleChangeSearch(event) {
    setSearchTerm(event.target.value)
  }

  function handleNew() {
    setDialogOpen(true)
    setCurrentDriver({})
  }

  function handleEdit(driver) {
    setDialogOpen(true)
    setCurrentDriver(driver)
  }

  function handleClose() {
    setDialogOpen(false)
    setCurrentDriver(null)
  }

  async function handleSave(data) {
    if (currentDriver?.id) {
      const updatedDriver = await updateDriver(currentDriver.id, data)

      if (updatedDriver) {
        driversList.updateItem(currentDriver.id, updatedDriver)
        handleClose()
      }
    } else {
      const newDriver = await createDriver(data)

      if (newDriver) {
        driversList.addItem(newDriver)
        handleClose()
      }
    }
  }

  function loadDrivers() {
    dispatch(getDriversRequest())
  }

  useEffect(() => {
    loadDrivers()
  }, [])

  useEffect(() => {
    driversList.setList(drivers)
  }, [drivers, driversList.setList])

  return (
    <Box>
      <DriverFormEdit
        open={dialogOpen}
        current={currentDriver}
        onSave={handleSave}
        onClose={handleClose}
      />

      <Box marginBottom={2} display="flex" alignItems="center" gridGap="1rem">
        <Box flex={1}>
          <TextField
            fullWidth
            placeholder="Pesquisar"
            variant="outlined"
            size="small"
            //
            value={searchTerm}
            onChange={handleChangeSearch}
          />
        </Box>

        <Button
          onClick={handleNew}
          variant="contained"
          color="primary"
          startIcon={<UserPlus />}
        >
          Adicionar motorista
        </Button>
      </Box>

      {driversLoading && drivers.length === 0 && (
        <LinearProgress variant="indeterminate" />
      )}

      <StyledTableContainer>
        <Table size="medium" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width="50%">Nome</TableCell>
              <TableCell width="20%">CPF</TableCell>
              <TableCell width="20%">RG</TableCell>
              <TableCell align="center" width="10%">
                Status
              </TableCell>
              <TableCell align="center" width="5%" />
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredDrivers.map((driver) => {
              return (
                <TableRow key={driver.id}>
                  <TableCell>{driver.wem_nome}</TableCell>
                  <TableCell>{formatter(driver.wem_cpf).toCPF()}</TableCell>
                  <TableCell>
                    {driver.wem_rg ? formatter(driver.wem_rg).toRG() : '-'}
                  </TableCell>

                  <TableCell align="center">
                    {driver.wem_ativo ? (
                      <Status
                        size="small"
                        mode="slim"
                        label="Ativo"
                        className="MuiChip-filledSuccess"
                      />
                    ) : (
                      <Status
                        size="small"
                        mode="slim"
                        label="Inativo"
                        className="MuiChip-filledError"
                      />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <Box display="flex" alignItems="center" gridGap="1rem">
                      <Tooltip title="Editar motorista">
                        <IconButton
                          onClick={() => handleEdit(driver)}
                          size="small"
                          color="primary"
                        >
                          <PencilSimple size={20} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              )
            })}

            {driversList.list.length === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={5}>
                  {driversLoading
                    ? 'Buscando motoristas...'
                    : 'Nenhum motorista cadastrado. Clique em Adicionar motorista'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </Box>
  )
}
